import React from 'react';
import { Card, Form, Input, Button, InputNumber, Switch, Tabs, message, Divider } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

const SettingsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [generalForm] = Form.useForm();
  const [notificationForm] = Form.useForm();
  const [securityForm] = Form.useForm();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await api.get('/admin/settings');
      return response.data;
    }
  });

  const updateMutation = useMutation({
    mutationFn: async (values: any) => {
      console.log('[SettingsPage] Saving:', JSON.stringify(values));
      return api.post('/admin/settings', values);
    },
    onSuccess: () => {
      message.success('Ustawienia zapisane');
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: () => message.error('Błąd zapisywania'),
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (settings) {
      generalForm.setFieldsValue(settings.general);
      notificationForm.setFieldsValue(settings.notifications);
      if (settings.security) {
        securityForm.setFieldsValue(settings.security);
      }
    }
  }, [generalForm, notificationForm, securityForm, settings]);

  const handleGeneralSave = (values: any) => {
    updateMutation.mutate({ type: 'general', ...values });
  };

  const handleNotificationSave = (values: any) => {
    updateMutation.mutate({ type: 'notifications', ...values });
  };

  const handleSecuritySave = (values: any) => {
    updateMutation.mutate({ type: 'security', ...values });
  };

  const tabItems = [
    {
      key: 'general',
      label: 'Ogólne',
      children: (
        <Form form={generalForm} layout="vertical" onFinish={handleGeneralSave}>
          <Form.Item name="systemName" label="Nazwa systemu">
            <Input />
          </Form.Item>
          <Form.Item name="domain" label="Domena aplikacji">
            <Input />
          </Form.Item>
          <Form.Item name="timeFormat" label="Format czasu">
            <Input placeholder="24h lub 12h" />
          </Form.Item>
          <Form.Item name="timezone" label="Strefa czasowa">
            <Input placeholder="Europe/Warsaw" />
          </Form.Item>
          <Form.Item name="defaultEstimatedTime" label="Domyślny szacowany czas (min)">
            <InputNumber min={1} max={60} />
          </Form.Item>
          <Form.Item name="minHistoryForEstimate" label="Min. liczba historii do szacowania">
            <InputNumber min={1} max={100} />
          </Form.Item>
          <Form.Item name="pickupDeadlineMinutes" label="Czas na odbiór (min)">
            <InputNumber min={1} max={30} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={updateMutation.isPending}>
            Zapisz ustawienia
          </Button>
        </Form>
      ),
    },
    {
      key: 'notifications',
      label: 'Powiadomienia',
      children: (
        <Form form={notificationForm} layout="vertical" onFinish={handleNotificationSave}>
          <Form.Item name="pushEnabledClients" label="Powiadomienia push dla klientów" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="pushEnabledValets" label="Powiadomienia push dla lokajów" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Divider>Powitanie</Divider>
          <Form.Item name="notificationRequestAcceptedTitle" label="Żądanie przyjęte - tytuł">
            <Input />
          </Form.Item>
          <Form.Item name="notificationRequestAcceptedBody" label="Żądanie przyjęte - treść">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="notificationCarDeliveredTitle" label="Samochód przyprowadzony - tytuł">
            <Input />
          </Form.Item>
          <Form.Item name="notificationCarDeliveredBody" label="Samochód przyprowadzony - treść">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={updateMutation.isPending}>
            Zapisz szablony
          </Button>
        </Form>
      ),
    },
    {
      key: 'security',
      label: 'Bezpieczeństwo',
      children: (
        <Form form={securityForm} layout="vertical" onFinish={handleSecuritySave}>
          <Form.Item name="refreshTokenExpiryHours" label="Maksymalny czas nieaktywności (godziny)">
            <InputNumber min={1} max={720} />
          </Form.Item>
          <Form.Item name="minPasswordLength" label="Minimalna długość hasła">
            <InputNumber min={6} max={32} />
          </Form.Item>
          <Form.Item name="requireUppercase" label="Hasło musi zawierać wielkie litery" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="requireDigits" label="Hasło musi zawierać cyfry" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="requireSpecialChars" label="Hasło musi zawierać znaki specjalne" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={updateMutation.isPending}>Zapisz ustawienia</Button>
        </Form>
      ),
    },
  ];

  return (
    <div>
      <h1>Ustawienia</h1>
      <Card loading={isLoading}>
        <Tabs items={tabItems} />
      </Card>
    </div>
  );
};

export default SettingsPage;
